import { useEffect, useState } from 'react';
import { Star, Camera, X, ThumbsUp, ShieldCheck, Loader2, Filter } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = `${window.location.origin}/api`;
const MAX_PHOTOS = 4;

export default function ProductReviews({ productId, productName }) {
  const { user, isAuthenticated } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('recent');
  const [showForm, setShowForm] = useState(false);

  const load = (sortKey = sort) => {
    setLoading(true);
    axios.get(`${API}/products/${productId}/reviews?sort=${sortKey}`)
      .then(res => {
        setReviews(res.data.reviews || []);
        setStats(res.data.stats);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (productId) load(); /* eslint-disable-next-line */ }, [productId]);

  const changeSort = (s) => { setSort(s); load(s); };

  const markHelpful = async (reviewId) => {
    const key = `helpful_${reviewId}`;
    if (localStorage.getItem(key)) {
      toast.info('Already marked as helpful');
      return;
    }
    try {
      await axios.post(`${API}/products/${productId}/reviews/${reviewId}/helpful`);
      localStorage.setItem(key, '1');
      setReviews(prev => prev.map(r => r.id === reviewId ? { ...r, helpful_count: (r.helpful_count || 0) + 1 } : r));
    } catch {}
  };

  return (
    <section className="mt-16" data-testid="product-reviews">
      <div className="flex items-end justify-between gap-4 mb-5 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-8 h-[1px] bg-[#D4AF37]" />
          <h3 className="text-sm uppercase tracking-[0.2em] text-[#D4AF37] font-sans font-medium">Reviews</h3>
        </div>
        {isAuthenticated && !showForm && (
          <button
            onClick={() => setShowForm(true)}
            data-testid="write-review-btn"
            className="text-xs uppercase tracking-[0.2em] font-sans border border-[#1C7D3F]/40 text-[#1C7D3F] hover:bg-[#1C7D3F] hover:text-white px-4 py-2 transition-colors"
          >
            Write a Review
          </button>
        )}
      </div>

      {/* Stats summary */}
      {stats && (
        <div className="bg-[#141414] border border-white/5 p-4 sm:p-6 mb-6 grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-6 items-center" data-testid="review-stats">
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 mb-1">
              <span className="text-5xl font-light text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                {stats.count > 0 ? stats.average : '—'}
              </span>
              <span className="text-xl text-white/30 font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>/5</span>
            </div>
            <div className="flex items-center justify-center gap-0.5 mb-1">
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} className={`w-3.5 h-3.5 ${i <= Math.round(stats.average) ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-white/15'}`} />
              ))}
            </div>
            <p className="text-white/40 text-xs font-sans">{stats.count} review{stats.count !== 1 ? 's' : ''}</p>
          </div>
          <div className="space-y-1">
            {[5, 4, 3, 2, 1].map(rating => {
              const count = stats.breakdown?.[String(rating)] || 0;
              const pct = stats.count > 0 ? (count / stats.count * 100) : 0;
              return (
                <div key={rating} className="flex items-center gap-2 text-xs font-sans">
                  <span className="text-white/40 w-6">{rating}★</span>
                  <div className="flex-1 h-1.5 bg-white/5">
                    <div className="h-full bg-[#D4AF37]" style={{ width: `${pct}%` }} />
                  </div>
                  <span className="text-white/30 w-8 text-right">{count}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Form */}
      {showForm && isAuthenticated && (
        <ReviewForm
          productId={productId}
          productName={productName}
          onCancel={() => setShowForm(false)}
          onSuccess={() => { setShowForm(false); load(); toast.success('Review submitted — pending approval'); }}
        />
      )}

      {!isAuthenticated && !showForm && (
        <div className="bg-[#141414] border border-white/5 p-4 mb-6 text-center" data-testid="login-to-review">
          <p className="text-white/50 text-sm font-sans">
            Want to share your experience? <a href="/login" className="text-[#D4AF37] hover:text-white underline">Log in</a> to write a review.
          </p>
        </div>
      )}

      {/* Sort */}
      {reviews.length > 0 && (
        <div className="flex items-center gap-2 mb-4" data-testid="review-sort">
          <Filter className="w-3.5 h-3.5 text-white/40" />
          {[
            { key: 'recent', label: 'Most Recent' },
            { key: 'highest', label: 'Highest Rated' },
            { key: 'lowest', label: 'Lowest Rated' },
          ].map(opt => (
            <button
              key={opt.key}
              onClick={() => changeSort(opt.key)}
              data-testid={`review-sort-${opt.key}`}
              className={`text-[10px] uppercase tracking-[0.18em] font-sans px-2 py-1 transition-colors ${
                sort === opt.key ? 'text-[#D4AF37] border-b border-[#D4AF37]' : 'text-white/40 hover:text-white border-b border-transparent'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {/* Reviews list */}
      {loading ? (
        <p className="text-white/30 text-sm font-sans py-8 text-center">Loading reviews…</p>
      ) : reviews.length === 0 && !showForm ? (
        <div className="bg-[#141414] border border-white/5 p-8 text-center" data-testid="no-reviews">
          <p className="text-white/40 text-sm font-sans mb-1">No reviews yet</p>
          <p className="text-white/30 text-xs font-sans">Be the first to share your experience.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="reviews-list">
          {reviews.map(r => (
            <ReviewCard key={r.id} review={r} onHelpful={() => markHelpful(r.id)} />
          ))}
        </div>
      )}
    </section>
  );
}

function ReviewCard({ review, onHelpful }) {
  const [openPhoto, setOpenPhoto] = useState(null);
  const date = new Date(review.created_at).toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' });
  return (
    <div className="bg-[#141414] border border-white/5 p-4 sm:p-5" data-testid={`review-${review.id}`}>
      <div className="flex items-start justify-between gap-3 mb-2">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <p className="text-white text-sm font-sans font-medium">{review.customer_name}</p>
            {review.is_verified_buyer && (
              <span className="flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-[#1C7D3F] font-sans">
                <ShieldCheck className="w-3 h-3" /> Verified Buyer
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex">
              {[1, 2, 3, 4, 5].map(i => (
                <Star key={i} className={`w-3 h-3 ${i <= review.rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-white/15'}`} />
              ))}
            </div>
            <span className="text-white/30 text-[10px] font-sans">{date}</span>
          </div>
        </div>
      </div>
      {review.title && <p className="text-white text-sm font-sans font-medium mb-2">{review.title}</p>}
      <p className="text-white/70 text-sm font-sans leading-relaxed mb-3 whitespace-pre-line">{review.body}</p>

      {review.photos?.length > 0 && (
        <div className="flex gap-2 mb-3 flex-wrap">
          {review.photos.map((path, i) => {
            const url = path.startsWith('http') ? path : `${window.location.origin}/api/files/${path}`;
            return (
              <button key={i} onClick={() => setOpenPhoto(url)} data-testid={`review-photo-${review.id}-${i}`}>
                <img src={url} alt="" loading="lazy" className="w-16 h-16 object-cover border border-white/10 hover:border-[#D4AF37] transition-colors" />
              </button>
            );
          })}
        </div>
      )}

      <button
        onClick={onHelpful}
        data-testid={`review-helpful-${review.id}`}
        className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.15em] font-sans text-white/40 hover:text-[#D4AF37] transition-colors"
      >
        <ThumbsUp className="w-3 h-3" /> Helpful{review.helpful_count > 0 && ` (${review.helpful_count})`}
      </button>

      {openPhoto && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setOpenPhoto(null)}>
          <img src={openPhoto} alt="" className="max-w-full max-h-full object-contain" />
          <button className="absolute top-4 right-4 text-white/60 hover:text-white" onClick={() => setOpenPhoto(null)}>
            <X className="w-6 h-6" />
          </button>
        </div>
      )}
    </div>
  );
}

function ReviewForm({ productId, productName, onCancel, onSuccess }) {
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [photos, setPhotos] = useState([]); // [{ path, url }]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

  const handlePhotoSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    if (photos.length + files.length > MAX_PHOTOS) {
      toast.error(`Max ${MAX_PHOTOS} photos`);
      return;
    }
    setUploading(true);
    for (const f of files) {
      if (f.size > 5 * 1024 * 1024) {
        toast.error(`${f.name} is over 5MB`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', f);
      try {
        const res = await axios.post(`${API}/products/${productId}/reviews/photo`, fd, {
          headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
        });
        setPhotos(prev => [...prev, res.data]);
      } catch {
        toast.error(`Failed to upload ${f.name}`);
      }
    }
    setUploading(false);
    e.target.value = '';
  };

  const removePhoto = (path) => setPhotos(prev => prev.filter(p => p.path !== path));

  const submit = async (e) => {
    e.preventDefault();
    if (rating < 1) { toast.error('Please select a rating'); return; }
    if (body.trim().length < 10) { toast.error('Review must be at least 10 characters'); return; }
    setSubmitting(true);
    try {
      await axios.post(`${API}/products/${productId}/reviews`, {
        rating, title: title.trim(), body: body.trim(),
        photos: photos.map(p => p.path),
      }, { headers: getHeaders() });
      onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit review');
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={submit} className="bg-[#141414] border border-[#D4AF37]/30 p-5 sm:p-6 mb-6" data-testid="review-form">
      <p className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-sans mb-3">Reviewing {productName}</p>

      <label className="block mb-4">
        <span className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-sans block mb-2">Your Rating</span>
        <div className="flex gap-1" data-testid="rating-stars">
          {[1, 2, 3, 4, 5].map(i => (
            <button
              type="button"
              key={i}
              onClick={() => setRating(i)}
              data-testid={`star-${i}`}
              className="p-1 hover:scale-110 transition-transform"
            >
              <Star className={`w-7 h-7 ${i <= rating ? 'fill-[#D4AF37] text-[#D4AF37]' : 'text-white/20'}`} />
            </button>
          ))}
        </div>
      </label>

      <label className="block mb-3">
        <span className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-sans block mb-1.5">Title (optional)</span>
        <input
          type="text"
          value={title}
          maxLength={100}
          onChange={e => setTitle(e.target.value)}
          placeholder="A standout sillage…"
          data-testid="review-title-input"
          className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/20 focus:border-[#D4AF37] focus:outline-none font-sans"
        />
      </label>

      <label className="block mb-4">
        <span className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-sans block mb-1.5">Your Experience</span>
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          minLength={10}
          maxLength={2000}
          rows={5}
          placeholder="Share how this scent performed — projection, longevity, occasions you wore it…"
          data-testid="review-body-input"
          className="w-full bg-[#0A0A0A] border border-white/10 px-3 py-2 text-sm text-white placeholder:text-white/20 focus:border-[#D4AF37] focus:outline-none font-sans resize-none"
        />
        <span className="text-white/20 text-[10px] font-sans">{body.length}/2000 — min 10 chars</span>
      </label>

      <div className="mb-4">
        <span className="text-white/40 text-[10px] uppercase tracking-[0.2em] font-sans block mb-2">Add Photos (up to {MAX_PHOTOS})</span>
        <div className="flex gap-2 flex-wrap mb-2">
          {photos.map(p => (
            <div key={p.path} className="relative" data-testid={`uploaded-photo-${p.path.split('/').pop()}`}>
              <img src={p.url.startsWith('http') ? p.url : `${window.location.origin}${p.url}`} alt="" className="w-16 h-16 object-cover border border-white/10" />
              <button type="button" onClick={() => removePhoto(p.path)} className="absolute -top-1 -right-1 bg-black border border-white/20 text-white/70 rounded-full p-0.5 hover:bg-red-500/20 hover:text-white">
                <X className="w-2.5 h-2.5" />
              </button>
            </div>
          ))}
          {photos.length < MAX_PHOTOS && (
            <label className={`w-16 h-16 border border-dashed border-white/15 flex items-center justify-center cursor-pointer hover:border-[#D4AF37] transition-colors ${uploading ? 'opacity-50 pointer-events-none' : ''}`} data-testid="photo-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin text-white/40" /> : <Camera className="w-4 h-4 text-white/30" />}
              <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handlePhotoSelect} className="hidden" />
            </label>
          )}
        </div>
      </div>

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          data-testid="review-cancel-btn"
          className="px-4 py-2 text-[10px] uppercase tracking-[0.2em] font-sans border border-white/15 text-white/60 hover:text-white"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          data-testid="review-submit-btn"
          className="px-5 py-2 text-[10px] uppercase tracking-[0.2em] font-sans bg-[#1C7D3F] text-white hover:bg-[#2E9B50] transition-colors font-medium disabled:opacity-50"
        >
          {submitting ? 'Submitting…' : 'Submit Review'}
        </button>
      </div>
    </form>
  );
}
